import * as Dialog from "@radix-ui/react-dialog";
import classNames from "classnames";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import KuikaCloseButton from "../kuika-close-button/kuika-close-button";
import { KuikaPopupContainerContext } from "../kuika-popup-container/kuika-popup-container-context";
import KuikaSpinnerSmall from "../kuika-spinner-small/kuika-spinner-small";
import { isKitPopupOpen } from "./open-kit-popup-selector";
import "./kuika-drawer.scss";

export type KuikaDrawerPlacement = "right" | "left" | "top" | "bottom";

export interface IKuikaDrawerProps {
  /** Whether the drawer is visible. */
  isOpen?: boolean;
  /** Fired on close request (close button, mask click or ESC). */
  onClose?: () => void;
  /** Left-aligned title content of the header bar. Also the dialog's accessible name. */
  header?: React.ReactNode;
  /** Footer content (rendered in the sticky footer region). */
  footer?: React.ReactNode;
  children?: React.ReactNode;
  /** Drawer width for left/right placement. */
  width?: number | string;
  /** Drawer height for top/bottom placement. Defaults to "auto" (sized by its content). */
  height?: number | string;
  placement?: KuikaDrawerPlacement;
  /** Render the header close button. Defaults to true. */
  closable?: boolean;
  maskClosable?: boolean;
  /** Shows a spinner over the body content. */
  isLoading?: boolean;
  className?: string;
  bodyClassName?: string;
  zIndex?: number;
  /** Fired after the open/close transition ends — the reliable point to move focus. */
  afterOpenChange?: (open: boolean) => void;
  /**
   * Whether the rest of the page is shut out while the drawer is open. On by default: focus is
   * trapped and everything behind the scrim stops taking the pointer.
   *
   * Turn it off for a drawer that hosts a control whose popup mounts on `document.body` and does
   * not know about the kit's popup container — antd's Cascader, still used by the symbol
   * picker, is one. A modal drawer leaves such a popup painted but unable to take a click. Off,
   * the drawer draws its own scrim, and only that scrim or the close button dismisses it, so a
   * press inside the stray popup is not read as a press outside the drawer.
   */
  modal?: boolean;
  /**
   * How far this drawer steps aside while a drawer opened from inside it is showing, so the
   * stack reads as a stack rather than one panel exactly covering another. Measured toward the
   * page from the drawer's own edge.
   */
  push?: number;
}

interface IKuikaDrawerStackContext {
  /** Told by a drawer opened inside this one when it opens and when it goes away. */
  onChildOpenChange: (open: boolean) => void;
}

// The child drawer sits inside this drawer's React tree even though Radix portals its DOM to the
// body, so context still reaches it — which is how it can tell its parent to step aside.
const KuikaDrawerStackContext = createContext<IKuikaDrawerStackContext | undefined>(undefined);

/** The panel's inline style, plus the distance a drawer opened inside it pushes it aside. */
interface IKuikaDrawerContentStyle extends React.CSSProperties {
  "--kuika-drawer-push"?: string;
}

const isVerticalPlacement = (placement: KuikaDrawerPlacement): boolean => placement === "top" || placement === "bottom";

const hasAnimation = (element: HTMLElement): boolean => window.getComputedStyle(element).animationName !== "none";

const KuikaDrawer: React.FC<IKuikaDrawerProps> = ({
  isOpen = false,
  onClose,
  header,
  footer,
  children,
  width = 480,
  height = "auto",
  placement = "right",
  closable = true,
  maskClosable = false,
  isLoading = false,
  className,
  bodyClassName,
  zIndex,
  afterOpenChange,
  modal = true,
  push
}) => {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const showHeader: boolean = header !== undefined || closable;

  const parentStack: IKuikaDrawerStackContext | undefined = useContext(KuikaDrawerStackContext);
  const [openChildCount, setOpenChildCount] = useState<number>(0);

  useEffect(() => {
    if (!parentStack || !isOpen) return undefined;
    parentStack.onChildOpenChange(true);
    return (): void => parentStack.onChildOpenChange(false);
  }, [parentStack, isOpen]);

  const stackContext: IKuikaDrawerStackContext = useMemo(
    (): IKuikaDrawerStackContext => ({
      onChildOpenChange: (open: boolean): void => {
        setOpenChildCount((count: number): number => count + (open ? 1 : -1));
      }
    }),
    []
  );

  const isPushed: boolean = push !== undefined && openChildCount > 0;

  const contentStyle: IKuikaDrawerContentStyle = { zIndex };
  if (isPushed) {
    contentStyle["--kuika-drawer-push"] = `${push}px`;
  }
  if (isVerticalPlacement(placement)) {
    if (height !== "auto") {
      contentStyle.height = height;
    }
  } else {
    contentStyle.width = width;
  }

  // rc-trigger calls this once the trigger is in the DOM, so `contentRef` is normally attached; the
  // trigger's own ancestor covers a popup that asks while the drawer is still committing.
  const getPopupContainer = useCallback(
    (triggerNode?: HTMLElement): HTMLElement =>
      contentRef.current ?? triggerNode?.closest<HTMLElement>(".kuika-drawer") ?? document.body,
    []
  );

  const handleOpenChange = (open: boolean): void => {
    if (!open) {
      onClose?.();
    }
  };

  const handleOpenAutoFocus = (): void => {
    // Reduced motion still swaps in a fade (kuika-drawer.scss), so a real browser always has an
    // animation to end here — this branch is for environments with no computed animation at all
    // (jsdom, where scss is mocked out and getComputedStyle never reports one), where the drawer
    // is settled the instant it mounts. Deferred a microtask so Radix's own initial focus lands
    // first and a consumer moving focus here is not immediately overridden.
    if (afterOpenChange && contentRef.current && !hasAnimation(contentRef.current)) {
      queueMicrotask(() => afterOpenChange(true));
    }
  };

  const handleAnimationEnd = (event: React.AnimationEvent<HTMLDivElement>): void => {
    // Descendants' animations bubble here too; only the panel's own slide-in counts.
    if (event.target === event.currentTarget && event.currentTarget.dataset.state === "open") {
      afterOpenChange?.(true);
    }
  };

  const handleEscapeKeyDown = (event: KeyboardEvent): void => {
    // Escape inside an open select or menu closes that popup, not the whole drawer (and its form).
    if (isKitPopupOpen(contentRef.current)) {
      event.preventDefault();
    }
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleOpenChange} modal={modal}>
      <Dialog.Portal>
        {modal && <Dialog.Overlay className="kuika-drawer__overlay" style={{ zIndex }} />}
        {!modal &&
          isOpen && (
            // Radix draws no overlay for a non-modal dialog, so the drawer brings its own.
            <div
              className="kuika-drawer__overlay"
              data-state="open"
              style={{ zIndex }}
              onClick={maskClosable ? (): void => onClose?.() : undefined}
            />
          )}
        <Dialog.Content
          ref={contentRef}
          className={classNames("kuika-drawer", `kuika-drawer--${placement}`, className, {
            "kuika-drawer--pushed": isPushed
          })}
          style={contentStyle}
          aria-describedby={undefined}
          onOpenAutoFocus={handleOpenAutoFocus}
          // Unmounted after the exit animation, so this is the "closed and settled" moment.
          onCloseAutoFocus={() => afterOpenChange?.(false)}
          onAnimationEnd={handleAnimationEnd}
          onEscapeKeyDown={handleEscapeKeyDown}
          onPointerDownOutside={(event: Event) => {
            // A non-modal drawer draws its own scrim, so a press that reaches Radix as "outside"
            // is one that landed somewhere else entirely — a popup mounted on the body, most
            // likely — and must not close the drawer.
            if (!modal || !maskClosable) {
              event.preventDefault();
            }
          }}
          onInteractOutside={(event: Event) => {
            if (!modal) {
              event.preventDefault();
            }
          }}
          // Only a click on the mask may dismiss; focus drifting outside never does.
          onFocusOutside={(event: Event) => event.preventDefault()}
        >
          <KuikaPopupContainerContext.Provider value={getPopupContainer}>
            <KuikaDrawerStackContext.Provider value={stackContext}>
              <div className="kuika-drawer__layout">
                {showHeader && (
                  <div className="kuika-drawer__header">
                    <div className="kuika-drawer__header-bar">
                      <Dialog.Title asChild>
                        <span className="kuika-drawer__header-title">{header}</span>
                      </Dialog.Title>
                      {closable && (
                        <Dialog.Close asChild>
                          <KuikaCloseButton className="kuika-drawer__close" />
                        </Dialog.Close>
                      )}
                    </div>
                  </div>
                )}
                <div className={classNames("kuika-drawer__body", bodyClassName)}>
                  <KuikaSpinnerSmall spinning={isLoading}>{children}</KuikaSpinnerSmall>
                </div>
                {footer && <div className="kuika-drawer__footer">{footer}</div>}
              </div>
            </KuikaDrawerStackContext.Provider>
          </KuikaPopupContainerContext.Provider>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default KuikaDrawer;
